/**
 * Emergency contacts (patient): people to call in an emergency, one of them primary.
 *
 * The API returns each number in full (the dialer needs it) only to the patient who saved it, and
 * a masked form for screens to show until the patient asks to see it. Responses are no-store, and
 * the query keys include the user id, so a later sign-in on the same tab never reads another
 * account's cached list.
 */

import { apiFetch } from "./client";

export type ContactRelationship = "family" | "friend" | "doctor" | "other";

export interface EmergencyContact {
  id: string;
  name: string;
  phone: string;
  phone_masked: string;
  relationship: ContactRelationship;
  relationship_label: string | null;
  is_primary: boolean;
  created_at: string;
  updated_at: string;
}

export interface EmergencyContactInput {
  name: string;
  phone: string;
  relationship: ContactRelationship;
  relationship_label?: string | null;
  is_primary?: boolean;
}

export const emergencyContactsKey = (userId: string | undefined) => ["emergency-contacts", userId] as const;

export const listEmergencyContacts = (): Promise<EmergencyContact[]> =>
  apiFetch("/api/v1/me/emergency-contacts");

export const addEmergencyContact = (body: EmergencyContactInput): Promise<EmergencyContact> =>
  apiFetch("/api/v1/me/emergency-contacts", { method: "POST", body });

export const updateEmergencyContact = (
  id: string,
  body: Partial<Omit<EmergencyContactInput, "is_primary">>,
): Promise<EmergencyContact> => apiFetch(`/api/v1/me/emergency-contacts/${id}`, { method: "PATCH", body });

export const removeEmergencyContact = (id: string): Promise<void> =>
  apiFetch(`/api/v1/me/emergency-contacts/${id}`, { method: "DELETE" });

export const setPrimaryEmergencyContact = (id: string): Promise<EmergencyContact> =>
  apiFetch(`/api/v1/me/emergency-contacts/${id}/set-primary`, { method: "POST" });
